import { Box, type SxProps } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useRevealWage } from "../../../hooks/useRevealWage";
import { CostDeepDiveUnit, PushReportType } from "../../../utils/models";
import { useGetManagementCenterStaffMix, useManagementCenterFilters } from "../hooks";
import { type Grouping, ManagementCenterHeader } from "../ManagementCenterHeader";
import { MaximizeButton } from "../MaximizeButton";
import { MaximizedChartDialog } from "../MaximizedChartDialog";
import { useDownloadCostDeepDive } from "../useDownloadReport";
import { MC_PAGE_BG } from "../utils";
import { StaffMix } from "../visualizers/StaffMix";
import { CostDeepDiveChart } from "./CostDeepDiveChart";
import { CostDeepDiveHeader } from "./CostDeepDiveHeader";
import { CostDeepDiveTable } from "./CostDeepDiveTable";
import { useGetCostDeepDive } from "./hooks";

const containerSx = {
	height: "calc(100% - 40px)",
	width: "100%",
} as const satisfies SxProps;

export const CostDeepDiveReport: FC = () => {
	const { track, trackError } = useAnalytics();
	const {
		shiftHourOptions,
		shiftHoursUpdating,
		shiftHoursUnavailable,
		mcSettingsReady,
		sinceDate,
		endDate,
		onStartDateChange,
		onEndDateChange,
		selectedRoles,
		roleOptions,
		onRolesChange,
		appliedRoleFilter,
		selectedJobs,
		onJobsChange,
		appliedJobFilter,
		selectedShiftHours,
		onShiftHoursChange,
		shiftHoursForApi,
		isActual,
		onActualToggleChange,
		laborCostEnabled,
		onLaborCostToggle,
		includeHolidays,
		onIncludeHolidaysToggle,
		excludeHolidaysForApi,
		includeLateScheduled,
		onIncludeLateScheduledToggle,
		includeLateScheduledForApi,
		estimateMissingWage,
		onEstimateMissingWageToggle,
		onEstimateMissingWageNoticeDismissed,
		otMode,
		onOtModeChange,
		onAlignToDayView,
		onSaveMcDefaults,
		onRestoreMcSettings,
		searchParams,
	} = useManagementCenterFilters({ trackPrefix: "cost_deep_dive" });
	const { timezone } = useDepartmentFlags();
	const clampRange = useMemo(
		() => ({ start: sinceDate.setZone(timezone).startOf("day"), end: (endDate ?? DateTime.now().setZone(timezone)).startOf("day") }),
		[sinceDate, endDate, timezone],
	);
	const canViewWage = useRevealWage();
	// When wage access is revoked, force hours; when restored, default back to cost.
	const [userUnit, setUserUnit] = useState<CostDeepDiveUnit>(CostDeepDiveUnit.Cost);
	const unit: CostDeepDiveUnit = canViewWage ? userUnit : CostDeepDiveUnit.Hours;
	const setUnit = canViewWage ? setUserUnit : undefined;
	const [grouping, setGrouping] = useState<Grouping>(() => {
		const param = searchParams.get("grouping");
		return param === "daily" ? "daily" : "weekly";
	});
	const onGroupingToggle = useCallback(
		(val: Grouping) => {
			setGrouping(val);
			track("cost_deep_dive_grouping_changed", { grouping: val });
		},
		[track],
	);

	// Per-patient-day (PPD/HPPD) toggle — affects only the chart, not the detail table below.
	const [byCensus, setByCensus] = useState(false);

	const isWeekly = grouping === "weekly";
	const costDeepDiveArgs = {
		sinceDate,
		endDate,
		isActual,
		grouping: isWeekly,
		// The report has an explicit Daily/Weekly toggle — honor "Weekly" even on short ranges.
		forceWeekly: isWeekly,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		laborCost: laborCostEnabled,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		hideWage: !canViewWage,
		otMode,
		estimateMissingWage,
		// Wait for the persisted `mc_*` settings before fetching — otherwise the first request uses
		// `defaultSettings`' guesses and this page paints a cost figure it then has to correct.
		enabled: mcSettingsReady,
	};
	const { data, isLoading: isLoadingCostDeepDive, isError, error } = useGetCostDeepDive(costDeepDiveArgs);
	// Separate gated fetch for the per-census chart view so the table keeps showing raw cost.
	const { data: perCensusData } = useGetCostDeepDive({ ...costDeepDiveArgs, perCensus: true, enabled: byCensus && mcSettingsReady });
	const chartData = byCensus ? perCensusData : data;
	// A gated-off query reports `isLoading: false` with no data, so fold the wait into the flag the UI
	// already spins on — otherwise the settings round-trip renders an empty table instead of a skeleton.
	const isLoading = isLoadingCostDeepDive || !mcSettingsReady;
	const { data: staffMixData, isLoading: isLoadingStaffMixQuery } = useGetManagementCenterStaffMix({
		sinceDate,
		endDate,
		isActual,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		laborCost: laborCostEnabled,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		hideWage: !canViewWage,
		otMode,
		estimateMissingWage,
		enabled: mcSettingsReady,
	});
	const isLoadingStaffMix = isLoadingStaffMixQuery || !mcSettingsReady;

	const hasTrackedViewed = useRef(false);
	useEffect(() => {
		if (!hasTrackedViewed.current) {
			track("cost_deep_dive_viewed");
			hasTrackedViewed.current = true;
		}
	}, [track]);

	useEffect(() => {
		if (isError) trackError("fetch_cost_deep_dive", { error_message: (error as Error)?.message });
	}, [isError, error, trackError]);

	const { mutateAsync: downloadCostDeepDive, isPending: isDownloading } = useDownloadCostDeepDive();
	const onDownload = useCallback(
		() =>
			downloadCostDeepDive({
				startDate: sinceDate,
				endDate,
				isActual,
				shiftHours: shiftHoursForApi,
				roles: appliedRoleFilter,
				jobs: appliedJobFilter,
				laborCost: laborCostEnabled,
				excludeHolidays: excludeHolidaysForApi,
				includeLateScheduled: includeLateScheduledForApi,
				hideWage: !canViewWage,
				unit,
				otMode,
				estimateMissingWage,
			}),
		[
			downloadCostDeepDive,
			sinceDate,
			endDate,
			isActual,
			shiftHoursForApi,
			appliedRoleFilter,
			appliedJobFilter,
			laborCostEnabled,
			excludeHolidaysForApi,
			includeLateScheduledForApi,
			canViewWage,
			unit,
			otMode,
			estimateMissingWage,
		],
	);

	const [chartMaximized, setChartMaximized] = useState(false);
	const handleMaximize = useCallback(() => {
		setChartMaximized(true);
		track("cost_deep_dive_chart_maximize_opened");
	}, [track]);
	const handleCloseMaximize = useCallback(() => {
		setChartMaximized(false);
		track("cost_deep_dive_chart_maximize_closed");
	}, [track]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				bgcolor: MC_PAGE_BG,
				overflow: "auto",
				padding: 2,
				overflowX: "hidden",
			}}
		>
			<CostDeepDiveHeader unit={unit} />
			<ManagementCenterHeader
				onStartDateChange={onStartDateChange}
				onEndDateChange={onEndDateChange}
				isActual={isActual}
				onActualToggleChange={onActualToggleChange}
				onRolesChange={onRolesChange}
				selectedRoles={selectedRoles}
				roleOptions={roleOptions}
				selectedJobs={selectedJobs}
				onJobsChange={onJobsChange}
				grouping={grouping}
				onGroupingToggle={onGroupingToggle}
				shiftHourOptions={shiftHourOptions}
				shiftHoursUpdating={shiftHoursUpdating}
				shiftHoursUnavailable={shiftHoursUnavailable}
				selectedShiftHours={selectedShiftHours}
				onShiftHoursChange={onShiftHoursChange}
				onDownload={onDownload}
				isDownloading={isDownloading}
				pushReportType={PushReportType.cost_deep_dive}
				laborCostEnabled={laborCostEnabled}
				onLaborCostToggle={onLaborCostToggle}
				includeHolidays={includeHolidays}
				onIncludeHolidaysToggle={onIncludeHolidaysToggle}
				includeLateScheduled={includeLateScheduled}
				onIncludeLateScheduledToggle={onIncludeLateScheduledToggle}
				estimateMissingWage={estimateMissingWage}
				onEstimateMissingWageToggle={onEstimateMissingWageToggle}
				onEstimateMissingWageNoticeDismissed={onEstimateMissingWageNoticeDismissed}
				otMode={otMode}
				onOtModeChange={onOtModeChange}
				onAlignToDayView={onAlignToDayView}
				onSaveMcDefaults={onSaveMcDefaults}
				onRestoreMcSettings={onRestoreMcSettings}
			/>
			<Box
				sx={{
					display: "flex",
					gap: 2,
					mt: 2,
					alignItems: "flex-start",
					height: "calc(100% - 160px)",
					overflow: "clip",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						height: "100%",

						width: {
							xs: "540px",
							xxl: "740px",
						},
					}}
				>
					<CostDeepDiveChart
						data={chartData}
						laborCost={laborCostEnabled}
						trailing={<MaximizeButton onClick={handleMaximize} />}
						containerSx={containerSx}
						unit={unit}
						onUnitChange={setUnit}
						canToggleUnit={canViewWage}
						clampRange={clampRange}
						perCensus={byCensus}
						onPerCensusChange={setByCensus}
					/>
					<StaffMix data={staffMixData} isLoading={isLoadingStaffMix} isActual={isActual} containerSx={containerSx} clampRange={clampRange} />
				</Box>
				<CostDeepDiveTable
					data={data}
					isActual={isActual}
					isLoading={isLoading}
					isWeekly={isWeekly}
					sinceDate={sinceDate}
					endDate={endDate}
					roles={appliedRoleFilter}
					jobs={appliedJobFilter}
					laborCost={laborCostEnabled}
					shiftHours={shiftHoursForApi}
					includeLateScheduled={includeLateScheduledForApi}
					unit={unit}
					otMode={otMode}
					estimateMissingWage={estimateMissingWage}
				/>
			</Box>
			<MaximizedChartDialog open={chartMaximized} onClose={handleCloseMaximize}>
				<CostDeepDiveChart
					data={chartData}
					laborCost={laborCostEnabled}
					containerSx={{ width: "100%", height: "100%" }}
					unit={unit}
					onUnitChange={setUnit}
					canToggleUnit={canViewWage}
					clampRange={clampRange}
					perCensus={byCensus}
					onPerCensusChange={setByCensus}
				/>
			</MaximizedChartDialog>
		</Box>
	);
};
